import React from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { CpuIcon, Volume2Icon, VolumeXIcon } from 'lucide-react';
import { DayTimeline } from './DayTimeline';
import { HouseModeChip } from '../ui/HouseModeChip';
import { BeheerButton } from '../ui/BeheerButton';
import { NoodstopButton } from '../ui/NoodstopButton';
import { useDb } from '../../hooks/useDb';
import { useSpecSettings } from '../../contexts/SpecSettingsContext';
import { euro } from '../../utils/time';
import type { DeskStage } from './useDeskStage';
const GPU_TONE = {
  slaapt: 'bg-ink-muted',
  'wordt gewekt': 'bg-amber',
  actief: 'bg-accent'
} as const;

/** Top: day band, house mode, cloud cost vs budget, GPU-brein, Beheer and Noodstop. */
export function DeskTopBar({
  stage
}: {
  stage: DeskStage;
}) {
  const {
    soundOn,
    setSoundOn
  } = useSpecSettings();
  const navigate = useNavigate();
  const meta = useDb(t => t.meta);
  const ratio = meta.cloud.spent / meta.cloud.budget;
  const warn = ratio >= 0.8 || meta.cloud.localOnly;
  return <header className="relative z-20 flex flex-wrap items-center gap-x-6 gap-y-3 border-b border-white/[0.06] px-6 pb-3 pt-14" data-id="element-471" data-mp="1k414bh52lowf">
      <time className="font-display text-[34px] font-extralight leading-none tabular-nums text-ink" data-id="element-472" data-mp="1sz7bokjzof12">{meta.time}</time>
      <DayTimeline data-id="element-473" data-mp="1oy5c22m5u0yk" />
      <HouseModeChip mode={meta.mode} active data-id="element-474" data-mp="1oxm1j6yuaeuk" />

      <div className="w-[190px]" data-id="element-475" data-mp="11787xc0lw7xv">
        <div className="flex items-baseline justify-between gap-2 text-[13px]" data-id="element-476" data-mp="118az7l1ucceb">
          <span className="text-ink-muted" data-id="element-477" data-mp="w96sr26awsro">Cloud vandaag</span>
          <span className={`tabular-nums ${warn ? 'text-amber' : 'text-ink-soft'}`} data-id="element-478" data-mp="warj0fll1pjq">
            {euro(meta.cloud.spent)} / {euro(meta.cloud.budget)}
          </span>
        </div>
        <div className="relative mt-1.5 h-1.5 overflow-hidden rounded-full bg-white/[0.08]" role="meter" aria-valuemin={0} aria-valuemax={meta.cloud.budget} aria-valuenow={meta.cloud.spent} aria-label="Cloudkosten vandaag" data-id="element-479" data-mp="15b7nnse2b2q6">
          <motion.div className={`h-full rounded-full ${warn ? 'bg-amber' : 'bg-accent'}`} initial={false} animate={{
          width: `${Math.min(100, ratio * 100)}%`
        }} transition={{
          duration: 0.3,
          ease: [0.23, 1, 0.32, 1]
        }} data-id="element-480" data-mp="1eakncdmic6w4" />
          <span aria-hidden className="absolute inset-y-0 left-[80%] w-px bg-white/40" data-id="element-481" data-mp="1s9byps9zjegk" />
        </div>
        {meta.cloud.localOnly && <p className="mt-1 text-[13px] text-amber" data-id="element-482" data-mp="vi9ocuuz4t3a">Rest van de dag lokaal</p>}
      </div>

      <div className="flex items-center gap-2 whitespace-nowrap text-[13px] text-ink-soft" data-id="element-483" data-mp="zm7on8w1c9xx">
        <CpuIcon className="h-4 w-4 text-ink-muted" aria-hidden data-id="element-484" data-mp="14jho7hd521zh" />
        GPU-brein
        <span className={`h-2 w-2 rounded-full ${GPU_TONE[meta.gpu]}`} aria-hidden data-id="element-485" data-mp="19cj4ntszu9l6" />
        <span className="text-ink" data-id="element-486" data-mp="bx55fwlr691b">{meta.gpu}</span>
      </div>

      <div className="ml-auto flex items-center gap-3" data-id="element-487" data-mp="gvgishb9b1oa">
        <button type="button" onClick={() => setSoundOn(!soundOn)} aria-label={soundOn ? 'Geluid en stem uit' : 'Geluid en stem aan'} className="grid h-10 w-10 place-items-center rounded-full text-ink-muted transition-colors duration-150 hover:bg-white/[0.06] hover:text-ink" data-id="element-488" data-mp="gtvn2od7hcou">
          {soundOn ? <Volume2Icon className="h-5 w-5" data-id="element-489" data-mp="3rhk969opiabo" /> : <VolumeXIcon className="h-5 w-5" data-id="element-490" data-mp="3ribwkx85h1qv" />}
        </button>
        <BeheerButton onClick={() => navigate('/beheer/logboek')} data-id="element-491" data-mp="3mih26jp61u12" />
        <NoodstopButton onTrigger={stage.emergencyStop} data-id="element-492" data-mp="3mjs5yjim31s6" />
      </div>
    </header>;
}